import { useCallback, useMemo } from 'react';
import { FiBarChart2, FiDownload, FiEdit, FiRefreshCw } from 'react-icons/fi';
import { generatePath, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Flex,
  Grid,
  IconButton,
  Text,
  useToast,
} from '@chakra-ui/react';
import { EvaluationEvent } from '@keyops-cep/api-client';

import { KeyOpsLinkButton } from '../../../components/ButtonComponents';
import Loading from '../../../components/Loading';
import { filterResponses } from '../../../HOCs/Filter/FilterProvider';
import { useEngagementDetails } from '../../../hooks/useEngagementDetails';
import { useRefreshEvaluationEventResponses } from '../../../hooks/useEvaluationEventById';
import useFeatureFlags from '../../../hooks/useFeatureFlags';
import { exportSurveyResult } from '../../../utils/functions/export-survey';
import { formatDateWithTime } from '../../../utils/functions/format-date';
import i18n from '../../../utils/i18n';
import { EVALUATION_EVENT_EDIT } from '../../../utils/internal-routes.const';

type ListEventCardProps = {
  event: EvaluationEvent;
  onOpenQr: (event: EvaluationEvent) => void;
};

export const ListEventCard = ({ event, onOpenQr }: ListEventCardProps) => {
  const { mutate } = useRefreshEvaluationEventResponses(event.id);

  const navigate = useNavigate();
  const toast = useToast();

  const {
    engagementDetails: eventEngagementDetails,
    isLoading,
    isError,
  } = useEngagementDetails(event.engagementId);
  const { data: flags, isLoading: isFeatureFlagsLoading } = useFeatureFlags();

  const filteredResponses = useMemo(() => {
    if (!eventEngagementDetails) return [];
    return filterResponses(eventEngagementDetails, [
      {
        type: 'Event number',
        option: event.externalId,
        checked: true,
      },
    ]);
  }, [eventEngagementDetails, event.externalId]);

  // Action handlers
  const downloadEventResponses = useCallback(() => {
    if (isError) {
      toast({
        title: i18n.t('errorComponent.title'),
        variant: 'subtle',
        isClosable: true,
        status: 'error',
        position: 'top-right',
      });
      return;
    }

    if (eventEngagementDetails) {
      exportSurveyResult(
        {
          ...eventEngagementDetails,
          responses: filteredResponses,
        },
        `${event.name}-filtered-data.csv`,
        'text/csv;charset=utf-8;'
      );
    }
  }, [isError, eventEngagementDetails, toast, filteredResponses, event.name]);

  const fieldsToDisplay = useMemo(
    () => [
      {
        title: i18n.t('evaluationEvent.listCard.startDate'),
        value: formatDateWithTime(new Date(event.startDate)),
      },
      {
        title: i18n.t('evaluationEvent.listCard.endDate'),
        value: formatDateWithTime(new Date(event.endDate)),
      },
    ],
    [event.startDate, event.endDate]
  );

  const renderValue = (value?: string | null) => (
    <Text fontSize="md" color={value ? 'inherit' : 'gray.400'}>
      {value || i18n.t('common.N/A')}
    </Text>
  );

  if (isFeatureFlagsLoading) {
    return <Loading />;
  }

  return (
    <Card variant="elevated">
      <CardHeader>
        <Text size="md">{event.name}</Text>
        <Text color="gray.500">{event.externalId}</Text>
      </CardHeader>
      <Divider borderColor="gray.100" />
      <CardBody>
        {isLoading ? (
          <Loading />
        ) : (
          <Grid templateColumns="repeat(2, 1fr)" rowGap={4}>
            {fieldsToDisplay.map(({ title, value }) => (
              <Box key={title}>
                <Text color="gray.600" fontSize="sm">
                  {title}
                </Text>
                {renderValue(value)}
              </Box>
            ))}
          </Grid>
        )}
      </CardBody>
      <Divider borderColor="gray.100" />
      <CardFooter>
        <Flex w="100%" alignItems="center" justifyContent="space-between">
          <KeyOpsLinkButton
            fontWeight={400}
            _hover={{ textDecoration: 'none' }}
            onClick={() => onOpenQr(event)}
          >
            {i18n.t('evaluationEvent.listCard.getQrCode')}
          </KeyOpsLinkButton>
          <Flex alignItems="center">
            {filteredResponses.length > 0 && (
              <>
                <IconButton
                  variant="link"
                  color="keyops.blue"
                  aria-label="Download event results"
                  icon={<FiDownload fontSize={24} />}
                  onClick={downloadEventResponses}
                />
                <IconButton
                  variant="link"
                  color="keyops.blue"
                  aria-label="View event"
                  icon={<FiBarChart2 fontSize={24} />}
                  onClick={() =>
                    navigate(
                      `/engagements/${
                        event.engagementId
                      }/results/?filters=${encodeURIComponent(
                        'Event number'
                      )}:${encodeURIComponent(event.externalId)}`
                    )
                  }
                />
              </>
            )}
            {flags?.['Customer.ProgramEvaluation.ManualRefresh'] ? (
              <IconButton
                variant="link"
                color="keyops.blue"
                aria-label="Refresh responses"
                icon={<FiRefreshCw fontSize={22} />}
                onClick={() => mutate()}
              />
            ) : (
              <></>
            )}
            <IconButton
              variant="link"
              color="keyops.blue"
              aria-label="Edit event"
              icon={<FiEdit fontSize={22} />}
              onClick={() =>
                navigate(
                  generatePath(EVALUATION_EVENT_EDIT, {
                    eventId: event.id,
                  })
                )
              }
            />
          </Flex>
        </Flex>
      </CardFooter>
    </Card>
  );
};
